import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Database, Upload } from 'lucide-react';
import { getFooterSettings, footerToParams } from '@/components/FooterDateBar';
import { formatDate } from '@/lib/utils';

const fmtDate = (s) => {
  if (!s) return '–';
  return formatDate(s.replace(' ', 'T')) || s;
};

const ageDays = (s) => {
  if (!s) return null;
  const d = new Date(s.replace(' ', 'T'));
  return Math.floor((Date.now() - d.getTime()) / 86400000);
};

export default function DataStatusWidget() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [footer, setFooter] = useState(getFooterSettings);

  useEffect(() => {
    const h = (e) => { if (e.detail) setFooter(e.detail); };
    window.addEventListener('footer-settings-changed', h);
    return () => window.removeEventListener('footer-settings-changed', h);
  }, []);

  useEffect(() => {
    const params = footerToParams(footer);
    api.get('/filemaker-contacts/data-status', { params })
      .then((res) => setRows(res.data))
      .catch(() => setRows([]));
  }, [footer]);

  if (!rows || rows.length === 0) return null;

  const openTenant = (tid) => {
    localStorage.setItem('active_tenant_id', tid);
    window.location.href = '/unterrichtsverwaltung';
  };

  return (
    <Card className="mb-6 overflow-hidden border-brand-border" data-testid="data-status-widget">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2 text-brand-anthracite">
          <Database className="w-4 h-4 text-brand-red shrink-0" /> Datenstand je Mandant
        </CardTitle>
      </CardHeader>
      <CardContent className="min-w-0">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 min-w-0">
          {rows.map((r) => {
            const days = ageDays(r.last_import_at);
            const stale = days === null || days > 30;
            const importType = r.last_import_type === 'kontakte'
              ? 'Kontakte'
              : r.last_import_type === 'MSV-Import'
                ? 'MSV-Import'
                : r.last_import_type
                  ? 'Belegungen'
                  : null;
            return (
              <div
                key={r.tenant_id}
                onClick={() => openTenant(r.tenant_id)}
                title={`Zur Unterrichtsverwaltung von ${r.name} wechseln`}
                className="rounded-lg border border-brand-border bg-white p-3 cursor-pointer transition-colors hover:border-brand-red/40 hover:shadow-sm min-w-0 overflow-hidden border-l-4 border-l-brand-red"
                data-testid={`data-status-${r.tenant_id}`}
              >
                <div className="text-xs font-semibold text-brand-text break-words leading-snug" title={r.name}>
                  {r.name}
                </div>
                <div className="mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-0.5 min-w-0">
                  <span className="text-xl font-bold tabular-nums text-brand-anthracite">{r.active_enrollments}</span>
                  <span className="text-[11px] text-brand-muted">aktive Belegungen</span>
                </div>
                <div className="text-[11px] text-brand-muted">{r.active_students} aktive Schüler</div>
                <div className="mt-2 flex flex-col gap-1.5 min-w-0">
                  <span className="text-[11px] text-brand-muted break-words leading-snug">
                    Letzter Import: <b className="text-brand-text">{fmtDate(r.last_import_at)}</b>
                    {importType ? ` (${importType})` : ''}
                  </span>
                  {stale && (
                    <Badge
                      onClick={(e) => { e.stopPropagation(); navigate('/filemaker-import'); }}
                      variant="outline"
                      className="cursor-pointer border-brand-red text-brand-red hover:bg-brand-red/5 text-[10px] gap-1 w-fit max-w-full bg-white"
                    >
                      <Upload className="w-3 h-3 shrink-0" /> Abgleich
                    </Badge>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
